import React from 'react'
import { Box, Typography, Button, CircularProgress } from '@mui/material'
import { useApplyStage } from './hooks'
import { getStatusTableRows, getApplyResultTableRows } from './formatUtils'
import { directTreeAccessUrl } from 'infrastructure/api'
import { OpenJsonButton } from '../../utility/OpenJsonButton'
import { downloadJson } from 'shared/utils/fileExport'
import { SummaryTable } from '../../SummaryTable'

import type { StageProps } from './types'

export function ApplyStage({ treeId, versionId }: StageProps) {
  const {
    applyStatus,
    processResult,
    selectedNodeResults,
    applyResult,
    handleStartStage,
    canStart,
  } = useApplyStage(treeId, versionId)

  return (
    <Box border={1} borderColor="grey.300" borderRadius={1} p={2} mb={2}>
      <Typography variant="h6" gutterBottom sx={{ fontStyle: 'italic' }}>
        Apply
      </Typography>

      {processResult?.results?.[0]?.result && (
        <Box border={1} borderColor="grey.300" borderRadius={1} p={2} mb={2}>
          <Typography variant="body1">
            This will apply changes to{' '}
            {processResult.results[0].result.meta.tree_id ===
            processResult.results[0].result.meta.work_tree_id ? (
              <strong>original tree</strong>
            ) : (
              <strong>copy tree</strong>
            )}
            :{' '}
            <a
              href={directTreeAccessUrl({
                treeId: processResult.results[0].result.meta.work_tree_id,
                versionId: processResult.results[0].result.meta.work_tree_ver,
              })}
              target="_blank"
              rel="noopener noreferrer"
            >
              {directTreeAccessUrl({
                treeId: processResult.results[0].result.meta.work_tree_id,
                versionId: processResult.results[0].result.meta.work_tree_ver,
              })}
            </a>
          </Typography>
        </Box>
      )}

      {selectedNodeResults && (
        <Box border={1} borderColor="grey.300" borderRadius={1} p={2} mb={2}>
          <Typography variant="body1">
            <strong>Selected Corrections Summary:</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {Object.keys(selectedNodeResults).length} nodes will be updated with{' '}
            {Object.values(selectedNodeResults).reduce(
              (sum, corrections) => sum + corrections.length,
              0,
            )}{' '}
            total corrections
          </Typography>
        </Box>
      )}

      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleStartStage}
          disabled={!canStart}
          size="small"
        >
          Start
        </Button>
      </Box>

      {applyStatus && (
        <Box sx={{ mb: 1 }}>
          {!applyStatus.completed && (
            <Box sx={{ mb: 1 }}>
              <CircularProgress size={20} color="primary" />
            </Box>
          )}
          <SummaryTable
            rows={getStatusTableRows(applyStatus, null)}
            firstColumnSx={{ fontStyle: 'italic' }}
          />
        </Box>
      )}

      {applyResult && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <OpenJsonButton
              data={applyResult}
              disabled={!applyResult}
              buttonText="See JSON results"
            />
            <Button
              variant="text"
              size="small"
              onClick={() => {
                downloadJson(
                  `apply_results_${treeId}_${versionId}.json`,
                  applyResult,
                )
              }}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              Download JSON
            </Button>
          </Box>
          <SummaryTable
            header="Apply Results"
            rows={getApplyResultTableRows(applyResult)}
            firstColumnSx={{ fontStyle: 'italic' }}
          />
        </>
      )}
    </Box>
  )
}
